import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Divider,
    Chip,
    Paper
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { motion } from 'framer-motion'
import { styled } from '@mui/system'
import PropTypes from 'prop-types'

const TeamMemberCard = styled(Card)(({ theme, role }) => ({
    position: 'relative',
    overflow: 'visible',
    borderRadius: '20px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    height: '100%',
    '&:hover': {
        transform: 'translateY(-10px)',
        boxShadow: theme.shadows[10],
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-8px',
        left: '-8px',
        right: '-8px',
        bottom: '-8px',
        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        borderRadius: '28px',
        zIndex: -1,
    },
}))

const TeamMember = ({ language = 'EN' }) => {
    const theme = useTheme()

    // Complete language content
    const content = {
        EN: {
            leadershipTitle: "Leadership Team",
            advisorsTitle: "Advisors & Team",
            credentialsLabel: "Credentials:",
            teamMembers: [
                {
                    name: "Darren Soo",
                    title: "Founder & CEO",
                    credentials: "TVET, SKM, VTO, TTT, eMBA, DBA",
                    bio: "11 years in education industry, 10 years in business consulting",
                    role: "founder",
                    image: "/images/team/darren-soo.jpg"
                },
                {
                    name: "Dr. Gavin Voon",
                    title: "CSO",
                    credentials: "21 years in global education",
                    bio: "Brand cultivation expert, international education strategist",
                    role: "cso",
                    image: "/images/team/gavin-voon.jpeg"
                },
                {
                    name: "Kelvin Tay",
                    title: "CTO",
                    credentials: "Bachelor in Business and IT",
                    bio: "10 years experience in IT industry specially in Mobile & Web App development",
                    role: "cto",
                    image: "/images/team/kelvin.png"
                },
                {
                    name: "Lawyer Shim",
                    title: "Legal Advisor",
                    credentials: "LLB (HONS), Internal Audit Officer",
                    bio: "Legal expert for Malaysia & Singapore operations",
                    role: "advisor",
                    image: "/images/team/lawyer-shim.png"
                },
                {
                    name: "Dr. Alice Liew",
                    title: "Education Advisor",
                    credentials: "TVET Doctorate in Beauty, Malaysia higher education certification",
                    bio: "10+ years developing international vocational courses",
                    role: "advisor",
                    image: "/images/team/alice-liew.png"
                },
                {
                    name: "Ruby Siah",
                    title: "Education Advisor",
                    credentials: "40 years in beauty industry",
                    bio: "Asia Pacific product brand distributor",
                    role: "advisor",
                    image: "/images/team/ruby-siah.png"
                },
                {
                    name: "Datin Dr.Winnie Loo",
                    title: "Strategy Advisor",
                    credentials: "Strategic Consultant- College Management",
                    bio: "Beauty Academy with over 30 years of experience in Malaysia chain brand",
                    role: "advisor",
                    image: "/images/team/winnie-loo.png"
                },
                {
                    name: "Dato Dr. Ho",
                    title: "Strategy Advisor",
                    credentials: "Strategic Consultant- Chain Planning",
                    bio: "Over 20 years of experience in chain planning, chain trainer recognized by the Malaysian government",
                    role: "advisor",
                    image: "/images/team/dr-ho.png"
                },
                {
                    name: "Peter Wong",
                    title: "Operation Advisor",
                    credentials: "Over 30 years in academy management",
                    bio: "Strategic consultant for academy operations",
                    role: "advisor",
                    image: "/images/team/peter-wong.png"
                },
                {
                    name: "Suki Tay",
                    title: "Diplomacy Advisor",
                    credentials: "10 years in corporate education activities",
                    bio: "Strategic consultant for domestic market diplomacy",
                    role: "advisor",
                    image: "/images/team/suki-tay.png"
                },
                {
                    name: "Suet Yee",
                    title: "Admin Officer - MY & SG",
                    credentials: "Internal Affair Officer",
                    bio: "BBA(HONS)",
                    role: "advisor",
                    image: "/images/team/suet-yee.png"
                }
            ]
        },
        BM: {
            leadershipTitle: "Pasukan Kepimpinan",
            advisorsTitle: "Penasihat & Pasukan",
            credentialsLabel: "Kelayakan:",
            teamMembers: [
                {
                    name: "Darren Soo",
                    title: "Pengasas & CEO",
                    credentials: "TVET, SKM, VTO, TTT, eMBA, DBA",
                    bio: "11 tahun dalam industri pendidikan, 10 tahun dalam perundingan perniagaan",
                    role: "founder",
                    image: "/images/team/darren-soo.jpg"
                },
                {
                    name: "Dr. Gavin Voon",
                    title: "Ketua Pegawai Strategik",
                    credentials: "21 tahun dalam pendidikan global",
                    bio: "Pakar pembangunan jenama, strategi pendidikan antarabangsa",
                    role: "cso",
                    image: "/images/team/gavin-voon.jpeg"
                },
                {
                    name: "Kelvin Tay",
                    title: "Ketua Pegawai Teknologi",
                    credentials: "Ijazah Sarjana Muda Perniagaan dan IT",
                    bio: "10 tahun pengalaman dalam industri IT khususnya pembangunan Aplikasi Mudah Alih & Web",
                    role: "cto",
                    image: "/images/team/kelvin.png"
                },
                {
                    name: "Peguam Shim",
                    title: "Penasihat Undang-undang",
                    credentials: "LLB (KEPUJIAN), Pegawai Audit Dalaman",
                    bio: "Pakar undang-undang untuk operasi Malaysia & Singapura",
                    role: "advisor",
                    image: "/images/team/lawyer-shim.png"
                },
                {
                    name: "Dr. Alice Liew",
                    title: "Penasihat Pendidikan",
                    credentials: "Doktor TVET dalam Kecantikan, pensijilan pendidikan tinggi Malaysia",
                    bio: "10+ tahun membangunkan kursus vokasional antarabangsa",
                    role: "advisor",
                    image: "/images/team/alice-liew.png"
                },
                {
                    name: "Ruby Siah",
                    title: "Penasihat Pendidikan",
                    credentials: "40 tahun dalam industri kecantikan",
                    bio: "Pengedar jenama produk Asia Pasifik",
                    role: "advisor",
                    image: "/images/team/ruby-siah.png"
                },
                {
                    name: "Datin Dr.Winnie Loo",
                    title: "Penasihat Strategik",
                    credentials: "Perunding Strategik - Pengurusan Kolej",
                    bio: "Akademi Kecantikan dengan lebih 30 tahun pengalaman dalam jenama rangkaian Malaysia",
                    role: "advisor",
                    image: "/images/team/winnie-loo.png"
                },
                {
                    name: "Dato Dr. Ho",
                    title: "Penasihat Strategik",
                    credentials: "Perunding Strategik - Perancangan Rangkaian",
                    bio: "Lebih 20 tahun pengalaman dalam perancangan rangkaian, jurulatih rangkaian yang diiktiraf oleh kerajaan Malaysia",
                    role: "advisor",
                    image: "/images/team/dr-ho.png"
                },
                {
                    name: "Peter Wong",
                    title: "Penasihat Operasi",
                    credentials: "Lebih 30 tahun dalam pengurusan akademi",
                    bio: "Perunding strategik untuk operasi akademi",
                    role: "advisor",
                    image: "/images/team/peter-wong.png"
                },
                {
                    name: "Suki Tay",
                    title: "Penasihat Diplomasi",
                    credentials: "10 tahun dalam aktiviti pendidikan korporat",
                    bio: "Perunding strategik untuk diplomasi pasaran domestik",
                    role: "advisor",
                    image: "/images/team/suki-tay.png"
                },
                {
                    name: "Suet Yee",
                    title: "Pegawai Pentadbiran - MY & SG",
                    credentials: "Pegawai Hal Ehwal Dalaman",
                    bio: "BBA(KEPUJIAN)",
                    role: "advisor",
                    image: "/images/team/suet-yee.png"
                }
            ]
        },
        CN: {
            leadershipTitle: "领导团队",
            advisorsTitle: "顾问与团队",
            credentialsLabel: "资历:",
            teamMembers: [
                {
                    name: "Darren Soo",
                    title: "创始人兼首席执行官",
                    credentials: "TVET, SKM, VTO, TTT, eMBA, DBA",
                    bio: "11年教育行业经验，10年商业咨询经验",
                    role: "founder",
                    image: "/images/team/darren-soo.jpg"
                },
                {
                    name: "Gavin Voon博士",
                    title: "首席战略官",
                    credentials: "21年全球教育经验",
                    bio: "品牌培育专家，国际教育战略家",
                    role: "cso",
                    image: "/images/team/gavin-voon.jpeg"
                },
                {
                    name: "Kelvin Tay",
                    title: "首席技术官",
                    credentials: "商业与IT学士",
                    bio: "10年IT行业经验，专长移动和Web应用开发",
                    role: "cto",
                    image: "/images/team/kelvin.png"
                },
                {
                    name: "Shim律师",
                    title: "法律顾问",
                    credentials: "法学学士(荣誉)，内部审计官",
                    bio: "马来西亚和新加坡业务法律专家",
                    role: "advisor",
                    image: "/images/team/lawyer-shim.png"
                },
                {
                    name: "Alice Liew博士",
                    title: "教育顾问",
                    credentials: "美业TVET博士，马来西亚高等教育认证",
                    bio: "10多年国际职业课程开发经验",
                    role: "advisor",
                    image: "/images/team/alice-liew.png"
                },
                {
                    name: "Ruby Siah",
                    title: "教育顾问",
                    credentials: "40年美业行业经验",
                    bio: "亚太产品品牌经销商",
                    role: "advisor",
                    image: "/images/team/ruby-siah.png"
                },
                {
                    name: "Datin Winnie Loo博士",
                    title: "战略顾问",
                    credentials: "战略顾问-学院管理",
                    bio: "拥有30多年马来西亚连锁品牌经验的美业学院",
                    role: "advisor",
                    image: "/images/team/winnie-loo.png"
                },
                {
                    name: "Dato Ho博士",
                    title: "战略顾问",
                    credentials: "战略顾问-连锁规划",
                    bio: "20多年连锁规划经验，马来西亚政府认证的连锁培训师",
                    role: "advisor",
                    image: "/images/team/dr-ho.png"
                },
                {
                    name: "Peter Wong",
                    title: "运营顾问",
                    credentials: "30多年学院管理经验",
                    bio: "学院运营战略顾问",
                    role: "advisor",
                    image: "/images/team/peter-wong.png"
                },
                {
                    name: "Suki Tay",
                    title: "外交顾问",
                    credentials: "10年企业教育活动经验",
                    bio: "国内市场外交战略顾问",
                    role: "advisor",
                    image: "/images/team/suki-tay.png"
                },
                {
                    name: "Suet Yee",
                    title: "行政主任-马来西亚与新加坡",
                    credentials: "内部事务官",
                    bio: "工商管理学士(荣誉)",
                    role: "advisor",
                    image: "/images/team/suet-yee.png"
                }
            ]
        }
    }

    const currentContent = content[language] || content.EN

    // Separate team members by role
    const leadership = currentContent.teamMembers.filter(member => ['founder', 'cso', 'cto'].includes(member.role))
    const advisors = currentContent.teamMembers.filter(member => member.role === 'advisor')

    return (
        <Container sx={{ py: 8, position: 'relative' }}>
            {/* Background effects */}
            <Box sx={{
                position: 'absolute',
                top: '-50px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '200px',
                height: '200px',
                background: `radial-gradient(${theme.palette.primary.main} 0%, transparent 70%)`,
                opacity: 0.2,
                zIndex: -1
            }} />

            {/* Leadership Section */}
            <Typography variant="h2" align="center" gutterBottom sx={{ mb: 6 }}>
                <Box component="span" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                }}>
                    {currentContent.leadershipTitle}
                </Box>
            </Typography>

            <Grid container spacing={3} justifyContent="center" sx={{ mb: 6 }}>
                {leadership.map((member, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TeamMemberCard component={motion.div} whileHover={{ scale: 1.05 }} role={member.role} sx={{ maxWidth: 350, width: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{
                                    position: 'relative',
                                    mb: 3,
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    height: '280px',
                                    background: member.image ? `url(${member.image}) center/cover` : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%)'
                                    }
                                }}>
                                    <Chip
                                        label={member.role.toUpperCase()}
                                        sx={{
                                            position: 'absolute',
                                            top: 12,
                                            left: 12,
                                            background: member.role === 'founder' ?
                                                `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.light})` :
                                                `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.light})`,
                                            color: theme.palette.getContrastText(theme.palette.primary.main),
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            height: '24px'
                                        }}
                                    />
                                    <Box sx={{ position: 'absolute', bottom: 12, left: 12, color: 'white' }}>
                                        <Typography variant="h6">{member.name}</Typography>
                                        <Typography variant="body2">{member.title}</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ position: 'relative', mt: -3, mb: 1, px: 2, zIndex: 1 }}>
                                    <Paper elevation={3} sx={{ p: 2, borderRadius: '12px', background: theme.palette.background.paper }}>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            <Box component="span" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                {currentContent.credentialsLabel}
                                            </Box> {member.credentials}
                                        </Typography>
                                        <Divider sx={{ my: 1.5, background: `linear-gradient(to right, transparent, ${theme.palette.primary.main}, transparent)`, height: '1px' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {member.bio}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </CardContent>
                        </TeamMemberCard>
                    </Grid>
                ))}
            </Grid>

            {/* Advisors Section */}
            <Typography variant="h3" align="center" gutterBottom sx={{ mb: 4 }}>
                <Box component="span" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                }}>
                    {currentContent.advisorsTitle}
                </Box>
            </Typography>

            <Grid container spacing={3} justifyContent="center">
                {advisors.map((member, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TeamMemberCard component={motion.div} whileHover={{ scale: 1.05 }} role={member.role} sx={{ maxWidth: 320, width: '100%' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{
                                    position: 'relative',
                                    mb: 2,
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    height: '220px',
                                    background: member.image ? `url(${member.image}) center/cover` : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%)'
                                    }
                                }}>
                                    <Chip
                                        label={member.role.toUpperCase()}
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            left: 16,
                                            background: `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.light})`,
                                            color: theme.palette.getContrastText(theme.palette.secondary.main),
                                            fontWeight: 'bold',
                                            fontSize: '0.7rem',
                                            height: '24px'
                                        }}
                                    />
                                    <Box sx={{ position: 'absolute', bottom: 16, left: 16, color: 'white' }}>
                                        <Typography variant="h5">{member.name}</Typography>
                                        <Typography variant="body2">{member.title}</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ position: 'relative', mt: -4, mb: 2, px: 2, zIndex: 1 }}>
                                    <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', background: theme.palette.background.paper }}>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            <Box component="span" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                                                {currentContent.credentialsLabel}
                                            </Box> {member.credentials}
                                        </Typography>
                                        <Divider sx={{ my: 2, background: `linear-gradient(to right, transparent, ${theme.palette.secondary.main}, transparent)`, height: '1px' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {member.bio}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </CardContent>
                        </TeamMemberCard>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{
                position: 'absolute',
                right: '-100px',
                top: '30%',
                width: '200px',
                height: '200px',
                background: `radial-gradient(${theme.palette.secondary.main} 0%, transparent 70%)`,
                opacity: 0.1,
                zIndex: -1
            }} />
        </Container>
    )
}

TeamMember.propTypes = {
    language: PropTypes.oneOf(['EN', 'BM', 'CN'])
}

export default TeamMember